import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { RefreshCw, Loader2, X, Shield, Terminal, AlertTriangle, Power } from "lucide-react";
import baseapi from "../../../../../services/baseapi";
import { showSuccess, showError } from "../../../../../utils/toast";

import OverviewTab from "./OverviewTab";
import ThreatsTab from "./ThreatsTab";
import TrafficTab from "./TrafficTab";
import UsersTab from "./UsersTab";
import AppsTab from "./AppsTab";
import LogsTab from "./LogsTab";

// ── Types ─────────────────────────────────────────────────────────────────────
export interface FirewallLogEvent {
    organization_id: number;
    client_id: number;
    id: number;
    received_at: string;
    log_timestamp?: string;
    severity: string;
    action: string;
    src_ip: string;
    src_port: string;

    srcintf?: string;
    dst_ip: string;
    dst_port: string;
    dstintf?: string;
    protocol: string;
    app: string;
    service?: string;
    subtype?: string;
    policy_name?: string;
    policyid?: string;
    sentbyte?: number;
    rcvdbyte?: number;
    duration?: number;
    osname?: string;
    srcmac?: string;
    firewall_type: string;
    unauthuser?: string;
    srccountry?: string;
    dstcountry?: string;
    mastersrcmac: string;
    asset_id?: number | string;
}

export interface FwStats {
    total: number;
    allowed: number;
    blocked: number;
    by_action: { label: string; count: number }[];
    per_hour: { hour: string; total: number; blocked: number }[];
    top_users: { label: string; count: number }[];
}

export interface ThreatTimelinePoint {
    t: string;
    ssh: number;
    web: number;
    portscan: number;
}

export interface ThreatAttacker {
    ip: string;
    country: string;
    hits: number;
    services: string[];
    first_seen: string;
    last_seen: string;
}

export interface ThreatRecentAttack {
    id: number;
    received_at: string;
    srcip: string;
    srccountry: string;
    dstip: string;
    service: string;
    app: string;
    msg: string;

}

export interface ThreatLoginEvent {
    id: number;
    level: string;
    received_at: string;
    srcip?: string;
    logdesc?: string;
    msg: string;
    user?: string;
    method?: string;
    status?: string;
}

export interface FwThreats {
    summary: {
        external_attacks: number;
        unique_attackers: number;
        critical_threats: number;
        top_target_ip: string;
        top_target_hits: number;
        attack_countries: number;
        risky_app_events: number;
    };
    timeline: ThreatTimelinePoint[];
    countries: { country: string; hits: number; unique_ips: number }[];
    attackers: ThreatAttacker[];
    services: { service: string; hits: number; unique_ips: number }[];
    risky_apps: { app: string; events: number; users: string[] }[];
    recent_attacks: ThreatRecentAttack[];
    login_events: ThreatLoginEvent[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────
export const formatBytes = (b: any) => {
    if (!b || b === 0 || isNaN(Number(b))) return "0 B";
    const bytes = Number(b);
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1048576) return (bytes / 1024).toFixed(2) + " KB";
    if (bytes < 1073741824) return (bytes / 1048576).toFixed(2) + " MB";
    return (bytes / 1073741824).toFixed(2) + " GB";
};

export const getSeverityStyle = (severity: string) => {
    const s = severity?.toLowerCase() || "";
    if (s === "critical" || s === "alert" || s === "emergency") {
        return "text-red-400 bg-red-400/10 border-red-500/20";
    }
    if (s === "high" || s === "error" || s === "critical") {
        return "text-orange-400 bg-orange-400/10 border-orange-500/20";
    }
    if (s === "medium" || s === "warning") {
        return "text-yellow-400 bg-yellow-400/10 border-yellow-500/20";
    }
    if (s === "low" || s === "notice" || s === "information" || s === "info") {
        return "text-emerald-400 bg-emerald-400/10 border-emerald-500/20";
    }
    return "text-gray-300 bg-gray-500/10 border-gray-500/20";
};

export const getActionStyle = (action: string) => {
    const a = action?.toLowerCase() || "";
    if (a === "deny" || a === "block" || a === "drop") {
        return "text-red-400 bg-red-400/10 border-red-500/20";
    }
    if (a === "allow" || a === "permit" || a === "accept" || a === "pass") {
        return "text-emerald-400 bg-emerald-400/10 border-emerald-500/20";
    }
    return "text-gray-300 bg-gray-500/10 border-gray-500/20";
};

export const formatTime = (ts: string) => {
    if (!ts) return "—";
    try {
        const date = new Date(ts);
        if (isNaN(date.getTime())) return "—";
        return date.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false
        });
    } catch {
        return "—";
    }
};

export const getProtocolName = (proto: string) => {
    if (!proto) return "—";
    const p = String(proto).trim();
    if (p === "6") return "TCP";
    if (p === "17") return "UDP";
    if (p === "1") return "ICMP";
    return `PROTO-${p}`;
};

// Drawer style mappings
const FIELD_LABELS: Record<string, string> = {
    action: "Action Taken",
    status: "Status",
    reason: "Reason",
    level: "Severity Level",
    severity: "Severity Level",
    crscore: "Criticality Score",
    crlevel: "Criticality Level",
    craction: "Criticality Action",
    msg: "Message / Alert",
    logdesc: "Log Description",
    virusname: "Threat / Virus Name",
    threattype: "Threat Type",
    attack: "Attack Vector",
    ref: "External Reference Link",
    user: "Identity User",
    unauthuser: "Unauthenticated User",
    unauthusersource: "Unauth User Source",
    srcname: "Source Hostname",
    osname: "OS Name",
    srcswversion: "OS Software Version",
    mastersrcmac: "Source Master MAC",
    srcmac: "Source MAC Address",
    agent: "Agent / Client Type",
    srcip: "Source IP",
    src_ip: "Source IP",
    srcport: "Source Port",
    src_port: "Source Port",
    srcintf: "Source Interface",
    srcintfrole: "Source Interface Role",
    srccountry: "Source Country",
    srcuuid: "Source UUID",
    dstip: "Destination IP",
    dst_ip: "Destination IP",
    dstport: "Destination Port",
    dst_port: "Destination Port",
    dstintf: "Destination Interface",
    dstintfrole: "Destination Interface Role",
    dstcountry: "Destination Country",
    dstuuid: "Destination UUID",
    proto: "Protocol Protocol",
    protocol: "Protocol Protocol",
    service: "Service / Port Name",
    sessionid: "Session Unique ID",
    duration: "Duration Time",
    sentbyte: "Bytes Sent",
    rcvdbyte: "Bytes Received",
    trandisp: "NAT Translation Mode",
    transip: "NAT Translate IP",
    app: "Application Name",
    appcat: "Application Category",
    apprisk: "Application Threat Risk",
    appid: "Application System ID",
    applist: "Application Profile List",
    hostname: "Domain Hostname",
    url: "Full Request URL",
    urlcat: "URL Target Category",
    direction: "Traffic Direction",
    utmaction: "UTM Action",
    policyid: "Firewall Policy ID",
    policy_name: "Firewall Policy Name",
    policyname: "Firewall Policy Name",
    policytype: "Policy Scheme Type",
    poluuid: "Policy Scheme UUID",
    method: "Auth Method",
    ui: "User Interface Channel",
    devname: "Device System Name",
    devid: "Device Hardware ID",
    logid: "System Log ID",
    type: "Log Base Type",
    subtype: "Log Detail Subtype",
    vd: "Virtual Domain name",
    firewall_type: "Firewall Model / Vendor",
    received_at: "Platform Received at",
    log_timestamp: "Device Log Timestamp"
};

const getFieldStyleClass = (k: string, val: any) => {
    if (!val) return "text-gray-300";
    if (k === "action") {
        const a = String(val).toLowerCase();
        if (["deny", "drop", "block"].includes(a)) return "text-red-400 font-bold uppercase";
        if (["allow", "permit", "accept", "pass"].includes(a)) return "text-emerald-400 font-bold uppercase";
    }
    if (["level", "severity", "crlevel"].includes(k)) {
        const v = String(val).toLowerCase();
        if (["critical", "alert", "emergency", "high", "error"].includes(v)) return "text-red-400 font-bold uppercase animate-pulse";
        if (["elevated", "medium", "warning"].includes(v)) return "text-yellow-400 font-bold uppercase";
    }
    if (["srcip", "src_ip", "dstip", "dst_ip", "transip"].includes(k)) return "text-cyan-400 font-mono";
    if (["user", "unauthuser"].includes(k)) return "text-pink-400 font-semibold";
    return "text-gray-300";
};

export const ACTION_COLORS = ["#38bdf8", "#fb7185", "#fbbf24", "#34d399", "#818cf8", "#c084fc"];

export const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-slate-950/95 border border-slate-800 p-2.5 rounded-lg shadow-xl text-xs font-sans">
                <p className="text-gray-200 font-bold mb-1 font-mono">{label}</p>
                {payload.map((pld: any, index: number) => (
                    <div key={index} className="flex items-center gap-2 mt-0.5">
                        <span className="w-2 h-2 rounded-full font-sans shrink-0" style={{ backgroundColor: pld.color || pld.fill }} />
                        <span className="text-gray-300 font-medium capitalize">{pld.name}:</span>
                        <span className="text-white font-bold font-mono">{pld.value.toLocaleString()}</span>
                    </div>
                ))}
            </div>
        );
    }
    return null;
};

const FIELD_SECTIONS = [
    { title: "🔴 THREAT ASSESSMENT", icon: "text-red-400", keys: ["action", "status", "reason", "level", "crscore", "crlevel", "craction", "msg", "logdesc", "virusname", "threattype", "attack", "ref"] },
    { title: "👤 IDENTITY", icon: "text-pink-400", keys: ["user", "unauthuser", "unauthusersource", "srcname", "osname", "srcswversion", "mastersrcmac", "srcmac", "agent"] },
    { title: "📡 SOURCE", icon: "text-green-400", keys: ["srcip", "src_ip", "srcport", "src_port", "srcintf", "srcintfrole", "srccountry", "srcuuid"] },
    { title: "🎯 DESTINATION", icon: "text-blue-400", keys: ["dstip", "dst_ip", "dstport", "dst_port", "dstintf", "dstintfrole", "dstcountry", "dstuuid"] },
    { title: "🔗 CONNECTION", icon: "text-cyan-400", keys: ["proto", "protocol", "service", "sessionid", "duration", "sentbyte", "rcvdbyte", "trandisp", "transip"] },
    { title: "📱 APPLICATION", icon: "text-purple-400", keys: ["app", "appcat", "apprisk", "appid", "applist", "hostname", "url", "urlcat", "direction", "utmaction"] },
    { title: "🛡️ POLICY", icon: "text-yellow-400", keys: ["policyid", "policy_name", "policyname", "policytype", "poluuid", "method", "ui"] },
    { title: "🖥️ DEVICE", icon: "text-gray-200", keys: ["devname", "devid", "logid", "type", "subtype", "vd", "firewall_type", "received_at", "log_timestamp"] },
];

export default function FirewallLogs({ asset_id, client_id }: { asset_id: any; client_id: string }) {
    const [activeTab, setActiveTab] = useState<"overview" | "threats" | "traffic" | "users" | "apps" | "logs">("overview");
    const [hours, setHours] = useState<string>("1");

    // Overview Stats State (from API)
    const [statsData, setStatsData] = useState<FwStats | null>(null);
    const [statsLoading, setStatsLoading] = useState(true);

    // Live Logs Backend Configuration State (Enable/Disable)
    const [isConfigEnabled, setIsConfigEnabled] = useState<boolean>(false);
    const [configAgentId, setConfigAgentId] = useState<number | null>(null);
    const [configLoading, setConfigLoading] = useState<boolean>(false);

    // Threats Stats State
    const [threatsData, setThreatsData] = useState<FwThreats | null>(null);
    const [threatsLoading, setThreatsLoading] = useState(true);

    // Traffic Tab State
    const [trafficData, setTrafficData] = useState<any>(null);
    const [trafficLoading, setTrafficLoading] = useState(true);

    // Users Tab State
    const [usersData, setUsersData] = useState<any>(null);
    const [usersLoading, setUsersLoading] = useState(true);

    // Apps Tab State
    const [appsData, setAppsData] = useState<any>(null);
    const [appsLoading, setAppsLoading] = useState(true);

    // Live Event Stream State (Pure WebSocket feed)
    const [liveLogs, setLiveLogs] = useState<FirewallLogEvent[]>([]);
    const [isLive, setIsLive] = useState(true);
    const isLiveRef = useRef(isLive);
    useEffect(() => {
        isLiveRef.current = isLive;
    }, [isLive]);

    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(15);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [severityFilter, setSeverityFilter] = useState<string>("All");
    const [actionFilter, setActionFilter] = useState<string>("All");

    // Real-Time Traffic Timeline Buckets (Cumulative, never restricted by 1,000 table limit)
    const [timelineBuckets, setTimelineBuckets] = useState<Record<number, { label: string; total: number; blocked: number; time: number }>>({});
    // Detail Panel Drawer State
    const [selectedLogId, setSelectedLogId] = useState<number | null>(null);
    const [selectedLogDetail, setSelectedLogDetail] = useState<{ fields?: any; raw?: string; id?: number; received_at?: string } | null>(null);
    const [drawerLoading, setDrawerLoading] = useState(false);
    const [copiedLogId, setCopiedLogId] = useState<number | null>(null);

    // Column Selector
    const [showColumnSelector, setShowColumnSelector] = useState(false);
    const [visibleColumns, setVisibleColumns] = useState<string[]>([
        "user", "time", "severity", "action", "source", "destination", "protocol", "app_service"
    ]);

    // Keep active filters synced in a ref to avoid WebSocket listener recreation
    const filtersRef = useRef({ asset_id, client_id });
    useEffect(() => {
        filtersRef.current = { asset_id, client_id };
    }, [asset_id, client_id]);

    // Debounce Search Term
    useEffect(() => {
        const t = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setCurrentPage(1);
        }, 400);
        return () => clearTimeout(t);
    }, [searchTerm]);

    // Fetch Overview Stats (API for total events, allowed, blocked, policy enforcement)
    const fetchOverview = useCallback(async (silent: boolean = false) => {
        if (!silent) setStatsLoading(true);
        try {
            const res = await baseapi.get("/v3/fw/stats", {
                baseURL: window.location.origin,
                params: {
                    hours,
                    client_id,
                    ...(asset_id ? { asset_id } : {})
                }
            });
            if (res.data) {
                setStatsData(res.data);
            }
        } catch (e) {
            console.error("Failed to load firewall overview metrics", e);
        } finally {
            if (!silent) setStatsLoading(false);
        }
    }, [hours, client_id, asset_id]);

    const pollOverview = useCallback(() => {
        fetchOverview(true);
    }, [fetchOverview]);

    // Fetch Live Logs Configuration (GET /api/firewall_live_logs_configuration/)
    const fetchLiveLogsConfig = useCallback(async () => {
        if (!asset_id || !client_id) return;
        try {
            const res = await baseapi.get("/firewall_live_logs_configuration/", {
                params: {
                    asset_id: Number(asset_id),
                    client_id: Number(client_id)
                }
            });
            if (res.data && res.data.status && res.data.data) {
                setIsConfigEnabled(!!res.data.data.enabled);
                if (res.data.data.agent_id) {
                    setConfigAgentId(Number(res.data.data.agent_id));
                }
            } else if (res.data && res.data.data) {
                setIsConfigEnabled(!!res.data.data.enabled);
                if (res.data.data.agent_id) {
                    setConfigAgentId(Number(res.data.data.agent_id));
                }
            }
        } catch (err) {
            console.error("Failed to load firewall live logs configuration", err);
        }
    }, [asset_id, client_id]);

    useEffect(() => {
        fetchLiveLogsConfig();
    }, [fetchLiveLogsConfig]);

    // Toggle Live Logs Configuration (POST /api/firewall_live_logs_configuration/)
    const handleToggleLiveLogsConfig = async () => {
        if (configLoading || !asset_id || !client_id) return;
        setConfigLoading(true);
        const targetEnabled = !isConfigEnabled;
        try {
            let agentId = configAgentId;
            if (!agentId) {
                const info = JSON.parse(sessionStorage.getItem("internal_ids") || "{}");
                agentId = info.agent_id ? Number(info.agent_id) : 0;
            }

            const payload = {
                asset_id: Number(asset_id),
                agent_id: Number(agentId || 0),
                client_id: Number(client_id),
                enabled: targetEnabled
            };

            const res = await baseapi.post("/firewall_live_logs_configuration/", payload);
            if (res.data && (res.data.status || res.status === 200 || res.status === 201)) {
                setIsConfigEnabled(targetEnabled);
                showSuccess(res.data?.message || (targetEnabled ? "Live logs enabled successfully" : "Live logs disabled successfully"));
                fetchLiveLogsConfig();
            } else {
                showError(res.data?.message || "Failed to update firewall live logs configuration");
                fetchLiveLogsConfig();
            }
        } catch (err: any) {
            console.error("Failed to update firewall live logs configuration", err);
            showError(err.response?.data?.message || err.message || "Failed to update firewall live logs configuration");
            fetchLiveLogsConfig();
        } finally {
            setConfigLoading(false);
        }
    };

    // Real-time Network Traffic Volume timeline from continuous cumulative buckets
    const timelineData = useMemo(() => {
        let buckets: { label: string; total: number; blocked: number; time: number }[] = [];

        if (hours === "today") {
            const startOfToday = new Date();
            startOfToday.setHours(0, 0, 0, 0);
            buckets = Object.values(timelineBuckets).filter(b => b.time >= startOfToday.getTime());
        } else if (hours === "yesterday") {
            const startOfYesterday = new Date();
            startOfYesterday.setDate(startOfYesterday.getDate() - 1);
            startOfYesterday.setHours(0, 0, 0, 0);
            const endOfYesterday = new Date();
            endOfYesterday.setHours(0, 0, 0, 0);
            buckets = Object.values(timelineBuckets).filter(b => b.time >= startOfYesterday.getTime() && b.time < endOfYesterday.getTime());
        } else {
            const cutoff = Date.now() - (Number(hours) || 24) * 60 * 60 * 1000;
            buckets = Object.values(timelineBuckets).filter(b => b.time >= cutoff);
        }

        if (!buckets.length) return [];

        const sorted = buckets.sort((a, b) => a.time - b.time);

        if (sorted.length === 1) {
            const single = sorted[0];
            const prevTime = single.time - 60000;
            const prevLabel = new Date(prevTime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
            return [
                { label: prevLabel, total: 0, blocked: 0, time: prevTime },
                single
            ];
        }

        return sorted;
    }, [timelineBuckets, hours]);

    // Fetch Threats Stats
    const fetchThreats = useCallback(async (silent: boolean = false) => {
        if (!silent) setThreatsLoading(true);
        try {
            const res = await baseapi.get("/v3/fw/threats", {
                baseURL: window.location.origin,
                params: {
                    hours,
                    client_id,
                    ...(asset_id ? { asset_id } : {})
                }
            });
            if (res.data) {
                setThreatsData(res.data);
            }
        } catch (e) {
            console.error("Failed to load firewall threat analysis", e);
        } finally {
            if (!silent) setThreatsLoading(false);
        }
    }, [hours, client_id, asset_id]);

    const pollThreats = useCallback(() => {
        fetchThreats(true);
    }, [fetchThreats]);

    // Fetch Traffic Data
    const fetchTraffic = useCallback(async (silent: boolean = false) => {
        if (!silent) setTrafficLoading(true);
        try {
            const res = await baseapi.get("/v3/fw/traffic", {
                baseURL: window.location.origin,
                params: {
                    hours,
                    client_id,
                    ...(asset_id ? { asset_id } : {})
                }
            });
            if (res.data) {
                setTrafficData(res.data);
            }
        } catch (e) {
            console.error("Failed to load firewall traffic data", e);
        } finally {
            if (!silent) setTrafficLoading(false);
        }
    }, [hours, client_id, asset_id]);

    const pollTraffic = useCallback(() => {
        fetchTraffic(true);
    }, [fetchTraffic]);

    // Fetch Users Data
    const fetchUsers = useCallback(async (silent: boolean = false) => {
        if (!silent) setUsersLoading(true);
        try {
            const res = await baseapi.get("/v3/fw/users", {
                baseURL: window.location.origin,
                params: {
                    hours,
                    client_id,
                    ...(asset_id ? { asset_id } : {})
                }
            });
            if (res.data) {
                setUsersData(res.data);
            }
        } catch (e) {
            console.error("Failed to load firewall users data", e);
        } finally {
            if (!silent) setUsersLoading(false);
        }
    }, [hours, client_id, asset_id]);

    const pollUsers = useCallback(() => {
        fetchUsers(true);
    }, [fetchUsers]);

    // Fetch Apps Data
    const fetchApps = useCallback(async (silent: boolean = false) => {
        if (!silent) setAppsLoading(true);
        try {
            const res = await baseapi.get("/v3/fw/apps", {
                baseURL: window.location.origin,
                params: {
                    hours,
                    client_id,
                    ...(asset_id ? { asset_id } : {})
                }
            });
            if (res.data) {
                setAppsData(res.data);
            }
        } catch (e) {
            console.error("Failed to load firewall apps data", e);
        } finally {
            if (!silent) setAppsLoading(false);
        }
    }, [hours, client_id, asset_id]);

    const pollApps = useCallback(() => {
        fetchApps(true);
    }, [fetchApps]);

    // Fetch Detailed single log event when drawer is opened
    const fetchLogDetail = useCallback(async (logId: number) => {
        setDrawerLoading(true);
        setSelectedLogDetail(null);
        try {
            const res = await baseapi.get(`/v3/fw/logs/${logId}`, {
                baseURL: window.location.origin,
                params: {
                    client_id,
                    ...(asset_id ? { asset_id } : {})
                }
            });
            if (res.data) {
                setSelectedLogDetail(res.data);
            }
        } catch (e) {
            console.error("Failed to load full detailed syslog event", e);
            // Fallback to locally cached logs
            const localCached = liveLogs.find(l => l.id === logId);
            if (localCached) {
                setSelectedLogDetail({ fields: localCached, id: logId, received_at: localCached.received_at });
            }
        } finally {
            setDrawerLoading(false);
        }
    }, [client_id, asset_id, liveLogs]);

    // Sync stats and threats data lazily when the activeTab changes
    useEffect(() => {
        if (activeTab === "overview") {
            fetchOverview();
        } else if (activeTab === "threats") {
            fetchThreats();
        } else if (activeTab === "traffic") {
            fetchTraffic();
        } else if (activeTab === "users") {
            fetchUsers();
        } else if (activeTab === "apps") {
            fetchApps();
        }
    }, [activeTab, fetchOverview, fetchThreats, fetchTraffic, fetchUsers, fetchApps]);

    // Detail drawer loading effect
    useEffect(() => {
        if (selectedLogId !== null) {
            fetchLogDetail(selectedLogId);
        }
    }, [selectedLogId, fetchLogDetail]);

    // WebSocket Live stream listener with 250ms batching buffer for smooth performance
    useEffect(() => {
        let ws: WebSocket | null = null;
        let reconnectTimeout: any = null;
        let batchInterval: any = null;
        let isClosed = false;
        let logCounter = 0;
        const incomingBuffer: FirewallLogEvent[] = [];

        // Batch flush into liveLogs and accumulate into timelineBuckets every 250ms
        batchInterval = setInterval(() => {
            if (incomingBuffer.length > 0 && isLiveRef.current) {
                const chunk = incomingBuffer.splice(0, incomingBuffer.length);
                // 1. Table keeps rolling window of 1,000 logs
                setLiveLogs(prev => [...chunk.reverse(), ...prev].slice(0, 1000));

                // 2. Chart accumulates continuously without any 1,000 event limit
                setTimelineBuckets(prev => {
                    const next = { ...prev };
                    for (const log of chunk) {
                        const d = new Date(log.received_at || Date.now());
                        const ms = d.getTime();
                        if (isNaN(ms)) continue;

                        // 1-minute bucket resolution
                        const bucketKey = Math.floor(ms / 60000) * 60000;
                        const existing = next[bucketKey];
                        const bDate = new Date(bucketKey);
                        const label = existing ? existing.label : bDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

                        const act = (log.action || "").toLowerCase();
                        const isBlocked = ["deny", "block", "drop"].includes(act);

                        next[bucketKey] = {
                            label,
                            total: (existing ? existing.total : 0) + 1,
                            blocked: (existing ? existing.blocked : 0) + (isBlocked ? 1 : 0),
                            time: bucketKey
                        };
                    }
                    return next;
                });
            }
        }, 250);

        const connectWs = () => {
            if (isClosed) return;
            const token = localStorage.getItem('access_token');
            const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
            const host = window.location.host;
            const wsUrl = `${protocol}//${host}/ws/firewall_log_events?token=${encodeURIComponent(token || '')}&client_id=${client_id}`;

            ws = new WebSocket(wsUrl);

            ws.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    const rawItems = Array.isArray(data)
                        ? data
                        : Array.isArray(data?.data)
                            ? data.data
                            : Array.isArray(data?.records)
                                ? data.records
                                : [data?.record || data?.data || data];

                    for (const item of rawItems) {
                        if (!item || typeof item !== "object") continue;
                        // Always generate a unique ID so streaming logs with identical FortiGate logid aren't dropped
                        const uniqueId = item.id !== undefined && item.id !== null
                            ? item.id
                            : `${Date.now()}_${++logCounter}_${Math.random().toString(36).slice(2, 7)}`;
                        const newLog: FirewallLogEvent = { ...item, id: uniqueId };

                        const { asset_id: curAsset } = filtersRef.current;
                        const itemAsset = newLog.asset_id ?? (newLog as any).assetId;
                        if (curAsset && itemAsset !== undefined && itemAsset !== null && Number(itemAsset) !== Number(curAsset)) {
                            continue;
                        }

                        // If feed is currently live, queue into batch buffer
                        if (isLiveRef.current) {
                            incomingBuffer.push(newLog);
                        }
                    }
                } catch (err) {
                    console.error("Failed to parse event message:", err);
                }
            };

            ws.onclose = () => {
                ws = null;
                if (!isClosed) {
                    reconnectTimeout = setTimeout(connectWs, 3000);
                }
            };
        };

        connectWs();

        return () => {
            isClosed = true;
            if (batchInterval) clearInterval(batchInterval);
            if (reconnectTimeout) clearTimeout(reconnectTimeout);
            if (ws) {
                // Detach all listeners to prevent memory leaks and zombie callbacks
                ws.onopen = null;
                ws.onmessage = null;
                ws.onerror = null;
                ws.onclose = null;
                try {
                    if (ws.readyState === WebSocket.OPEN) {
                        ws.close(1000, "Component unmounted");
                    } else if (ws.readyState === WebSocket.CONNECTING) {
                        // Close gracefully once connected if user navigated away quickly
                        ws.onopen = () => {
                            try { ws?.close(1000, "Component unmounted"); } catch { }
                        };
                    }
                } catch (e) {
                    console.error("Error closing WebSocket on unmount:", e);
                }
                ws = null;
            }
        };
    }, [client_id]);

    // Instant local filtering over the WebSocket logs (used when paused)
    const filteredLogs = useMemo(() => {
        return liveLogs.filter(log => {
            // 1. Severity filter
            if (severityFilter && severityFilter !== "All") {
                const sFilter = severityFilter.toLowerCase();
                const logSev = (log.severity || "").toLowerCase();
                if (logSev !== sFilter) return false;
            }

            // 2. Action filter
            if (actionFilter && actionFilter !== "All") {
                const aFilter = actionFilter.toLowerCase();
                const logAct = (log.action || "").toLowerCase();
                if (aFilter === "accept") {
                    if (logAct !== "accept" && logAct !== "allow" && logAct !== "permit" && logAct !== "pass") return false;
                } else if (aFilter === "deny") {
                    if (logAct !== "deny" && logAct !== "block" && logAct !== "drop") return false;
                } else if (logAct !== aFilter) {
                    return false;
                }
            }

            // 3. Search Term filter
            if (searchTerm) {
                const term = searchTerm.toLowerCase();
                const matches = (
                    (log.src_ip && log.src_ip.toLowerCase().includes(term)) ||
                    (log.dst_ip && log.dst_ip.toLowerCase().includes(term)) ||
                    (log.protocol && log.protocol.toLowerCase().includes(term)) ||
                    (log.action && log.action.toLowerCase().includes(term)) ||
                    (log.app && log.app.toLowerCase().includes(term)) ||
                    (log.severity && log.severity.toLowerCase().includes(term)) ||
                    (log.firewall_type && log.firewall_type.toLowerCase().includes(term)) ||
                    (log.unauthuser && log.unauthuser.toLowerCase().includes(term))
                );
                if (!matches) return false;
            }

            return true;
        });
    }, [liveLogs, severityFilter, actionFilter, searchTerm]);

    // Client-side pagination calculations over the paused feed
    const totalRecords = isLive ? liveLogs.length : filteredLogs.length;
    const totalPages = Math.ceil(totalRecords / limit) || 1;
    const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * limit + 1;
    const endRecord = Math.min(currentPage * limit, totalRecords);

    // Active logs selector: in live mode, displays rolling live feed directly without filters; in paused mode, displays locally filtered & paginated slice
    const activeLogs = useMemo(() => {
        if (isLive) {
            return liveLogs.slice(0, 100);
        }
        return filteredLogs.slice((currentPage - 1) * limit, currentPage * limit);
    }, [isLive, liveLogs, filteredLogs, currentPage, limit]);

    const isLogsLoading = false;

    const isTabLoading = useMemo(() => {
        if (activeTab === "overview") return statsLoading;
        if (activeTab === "threats") return threatsLoading;
        if (activeTab === "traffic") return trafficLoading;
        if (activeTab === "users") return usersLoading;
        if (activeTab === "apps") return appsLoading;
        if (activeTab === "logs") return isLogsLoading;
        return false;
    }, [activeTab, statsLoading, threatsLoading, trafficLoading, usersLoading, appsLoading, isLogsLoading]);

    // Sliding Detailed Sidebar Drawer Renderer
    const renderDetailDrawer = () => {
        if (selectedLogId === null) return null;

        const f = selectedLogDetail?.fields || {};
        const isVirus = !!f.virusname;
        const isDeny = ["deny", "drop", "block"].includes(String(f.action || "").toLowerCase());
        const isFail = String(f.status || "").toLowerCase() === "failed";

        return (
            <div className="fixed inset-y-0 right-0 w-[420px] bg-slate-950 border-l border-slate-800 shadow-[0_0_50px_rgba(0,0,0,0.8)] z-[9999] flex flex-col overflow-hidden animate-in slide-in-from-right duration-300">
                {/* Header */}
                <div className="p-4 border-b border-slate-800 bg-slate-950 flex items-center justify-between shrink-0">
                    <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-orange-500/10 border border-orange-500/20 rounded-lg">
                            <Shield className="w-4 h-4 text-orange-400" />
                        </div>
                        <div>
                            <h3 className="text-xs font-black text-white uppercase tracking-widest">LOG DETAILED INFO</h3>
                            <p className="text-[9px] text-slate-500 font-mono mt-0.5">ID: {selectedLogId}</p>
                        </div>
                    </div>
                    <button
                        onClick={() => {
                            setSelectedLogId(null);
                            setSelectedLogDetail(null);
                        }}
                        className="p-1.5 hover:bg-slate-900 text-slate-400 hover:text-white rounded-lg transition-colors focus:outline-none"
                    >
                        <X size={15} />
                    </button>
                </div>

                {/* Banner Section */}
                {!drawerLoading && selectedLogDetail && (isVirus || isDeny || isFail) && (
                    <div className={`px-4 py-2.5 text-[10px] font-black uppercase tracking-wider shrink-0 flex items-center gap-1.5 border-b border-slate-800/30
                        ${isVirus ? 'bg-red-500/15 text-red-400 border-red-500/20' :
                            isFail ? 'bg-red-500/15 text-red-400 border-red-500/20' :
                                'bg-red-500/10 text-orange-455 border-red-500/10'}`}
                    >
                        <AlertTriangle size={11} className="shrink-0" />
                        {isVirus && `⚠️ VIRUS DETECTED: ${f.virusname}`}
                        {isFail && `🔐 LOGIN FAILED ${f.user ? `— USER: ${f.user}` : ''} ${f.reason ? `— REASON: ${f.reason}` : ''}`}
                        {!isVirus && !isFail && isDeny && `🛡️ CONNECTION BLOCKED — ${f.msg || 'Connection denied by policy'}`}
                    </div>
                )}

                {/* Body Content */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scroll pb-16">
                    {drawerLoading ? (
                        <div className="h-48 flex flex-col items-center justify-center gap-2">
                            <Loader2 className="w-5 h-5 animate-spin text-orange-500" />
                            <span className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">Fetching Event Details...</span>
                        </div>
                    ) : !selectedLogDetail ? (
                        <div className="h-48 flex items-center justify-center text-[10px] text-slate-500 font-bold uppercase">No Detail Data Available</div>
                    ) : (
                        <>
                            {/* Status Highlights */}
                            <div className="grid grid-cols-2 gap-2.5">
                                <div className="bg-slate-900/30 border border-slate-800 p-2.5 rounded-lg flex flex-col items-center justify-center">
                                    <span className="text-[8px] text-slate-400 font-bold uppercase tracking-widest mb-1.5">Action</span>
                                    <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase border ${getActionStyle(f.action)}`}>
                                        {f.action || "—"}
                                    </span>
                                </div>
                                <div className="bg-slate-900/30 border border-slate-800 p-2.5 rounded-lg flex flex-col items-center justify-center">
                                    <span className="text-[8px] text-slate-400 font-bold uppercase tracking-widest mb-1.5">Severity</span>
                                    <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase border ${getSeverityStyle(f.level || f.severity)}`}>
                                        {f.level || f.severity || "—"}
                                    </span>
                                </div>
                            </div>

                            {/* Section Fields Map */}
                            {FIELD_SECTIONS.map((sec, secIdx) => {
                                const activeKeys = sec.keys.filter(k => f[k] !== undefined && f[k] !== null && f[k] !== "");
                                if (activeKeys.length === 0) return null;
                                return (
                                    <div key={secIdx} className="bg-slate-900/20 border border-slate-800/80 rounded-lg p-3 animate-fade-in">
                                        <h4 className={`text-[9px] font-black uppercase tracking-widest flex items-center gap-1.5 mb-2.5 border-b border-slate-800/60 pb-1.5 ${sec.icon}`}>
                                            {sec.title}
                                        </h4>
                                        <div className="grid grid-cols-2 gap-2">
                                            {activeKeys.map((k) => {
                                                const label = FIELD_LABELS[k] || k;
                                                const style = getFieldStyleClass(k, f[k]);
                                                const displayValue = ["sentbyte", "rcvdbyte"].includes(k) ? formatBytes(f[k]) : String(f[k]);
                                                return (
                                                    <div key={k} className="bg-slate-950/50 border border-slate-900 p-2 rounded-lg flex flex-col justify-between hover:border-slate-800 transition-colors">
                                                        <span className="text-[8px] text-slate-500 font-bold uppercase tracking-wider mb-1">{label}</span>
                                                        <span className={`text-[11px] font-semibold break-all leading-normal ${style}`}>{displayValue}</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}

                            {/* Other Unmapped Fields */}
                            {(() => {
                                const mappedSet = new Set(FIELD_SECTIONS.flatMap(s => s.keys));
                                const extraKeys = Object.keys(f).filter(k => !mappedSet.has(k) && f[k] !== undefined && f[k] !== null && f[k] !== "");
                                if (extraKeys.length === 0) return null;
                                return (
                                    <div className="bg-slate-900/20 border border-slate-800/80 rounded-lg p-3 animate-fade-in">
                                        <h4 className="text-[9px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5 mb-2.5 border-b border-slate-800/60 pb-1.5">
                                            📋 OTHER METADATA
                                        </h4>
                                        <div className="grid grid-cols-2 gap-2">
                                            {extraKeys.map((k) => (
                                                <div key={k} className="bg-slate-950/50 border border-slate-900 p-2 rounded-lg flex flex-col justify-between hover:border-slate-800 transition-colors">
                                                    <span className="text-[8px] text-slate-500 font-bold uppercase tracking-wider mb-1">{k}</span>
                                                    <span className="text-[10px] text-slate-350 font-mono font-medium break-all">{String(f[k])}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })()}

                            {/* Raw Syslog / Payload */}
                            {selectedLogDetail.raw && (
                                <div className="bg-slate-900/20 border border-slate-800/80 rounded-lg p-3 flex flex-col animate-fade-in">
                                    <div className="flex items-center justify-between border-b border-slate-800/60 pb-1.5 mb-2 shrink-0">
                                        <h4 className="text-[9px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                                            <Terminal size={11} className="text-slate-400" /> RAW SYSLOG PAYLOAD
                                        </h4>
                                        <button
                                            onClick={() => {
                                                navigator.clipboard.writeText(selectedLogDetail.raw || "");
                                                setCopiedLogId(selectedLogId);
                                                setTimeout(() => setCopiedLogId(null), 2000);
                                            }}
                                            className="text-[9px] font-black uppercase text-orange-500 hover:text-orange-400 focus:outline-none transition-colors"
                                        >
                                            {copiedLogId === selectedLogId ? "Copied!" : "Copy Payload"}
                                        </button>
                                    </div>
                                    <pre className="text-[9px] font-mono text-slate-350 bg-slate-950 p-2.5 rounded border border-slate-900 max-h-56 overflow-auto custom-scroll whitespace-pre-wrap leading-normal select-all">
                                        {selectedLogDetail.raw}
                                    </pre>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="flex flex-col bg-transparent text-gray-200">
            {/* Elegant Header with modern glass tab controls and sync triggers */}
            <div className="flex items-center gap-1.5 border-b border-slate-800/80 mb-5">
                {(["overview", "threats", "traffic", "users", "apps", "logs"] as const).map((tab) => {
                    const isActive = activeTab === tab;
                    return (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`relative px-5 py-3 text-xs font-black uppercase tracking-widest transition-all select-none focus:outline-none duration-250
                                ${isActive
                                    ? "text-orange-500"
                                    : "text-slate-200"
                                }`}
                        >
                            {tab === "overview" && "Overview"}
                            {tab === "threats" && "Threats"}
                            {tab === "traffic" && "Traffic"}
                            {tab === "users" && "Users"}
                            {tab === "apps" && "Apps"}
                            {tab === "logs" && "Live Logs"}
                            {isActive && (
                                <span className="absolute bottom-[-1px] left-3 right-3 h-0.5 bg-orange-500 rounded-t-full shadow-[0_0_8px_#f97316]" />
                            )}
                        </button>
                    );
                })}
                <div className="ml-auto flex items-center gap-2">
                    {activeTab !== "logs" && (
                        <div className="flex items-center gap-1.5 bg-slate-900/50 p-1 rounded-lg border border-slate-800/60">
                            <span className="text-[9px] font-bold text-slate-400 uppercase pl-1.5">Range:</span>
                            <select
                                value={hours}
                                onChange={(e) => setHours(e.target.value)}
                                className="bg-slate-950 border border-slate-800 text-[10px] font-bold text-gray-200 rounded px-2 py-1 select-none focus:outline-none focus:border-orange-500 w-24"
                            >
                                <option value="1">1h</option>
                                <option value="6">6h</option>
                                <option value="24">24h</option>
                                <option value="today">Today</option>
                                <option value="yesterday">Yesterday</option>
                            </select>
                        </div>
                    )}
                    {/* Live Logs Enable/Disable Button */}
                    <button
                        onClick={handleToggleLiveLogsConfig}
                        disabled={configLoading}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all select-none focus:outline-none border
                            ${isConfigEnabled
                                ? "bg-rose-500/10 hover:bg-rose-500/20 border-rose-500/30 hover:border-rose-500/50 text-rose-400"
                                : "bg-emerald-500/10 hover:bg-emerald-500/20 border-emerald-500/30 hover:border-emerald-500/50 text-emerald-400"
                            } disabled:opacity-50 disabled:cursor-not-allowed`}
                        title={isConfigEnabled ? "Click to disable live logs" : "Click to enable live logs"}
                    >
                        {configLoading ? (
                            <Loader2 size={12} className="animate-spin text-orange-400" />
                        ) : (
                            <Power size={12} className={isConfigEnabled ? "text-rose-400" : "text-emerald-400"} />
                        )}
                        <span>{isConfigEnabled ? "Disable" : "Enable"}</span>
                    </button>

                    <button
                        onClick={() => {
                            if (activeTab === "overview") {
                                fetchOverview();
                            } else if (activeTab === "threats") {
                                fetchThreats();
                            } else if (activeTab === "traffic") {
                                fetchTraffic();
                            } else if (activeTab === "users") {
                                fetchUsers();
                            } else if (activeTab === "apps") {
                                fetchApps();
                            }
                        }}
                        className="flex items-center justify-center p-1.5 bg-slate-900/60 hover:bg-slate-800/80 border border-slate-800 rounded-lg text-slate-400 hover:text-white transition-all focus:outline-none"
                    >
                        <RefreshCw size={13} className={isTabLoading ? "animate-spin text-orange-500" : ""} />
                    </button>
                </div>
            </div>

            {/* Conditional Tab Rendering */}
            {activeTab === "overview" && (
                <OverviewTab
                    statsData={statsData}
                    timelineData={timelineData}
                    liveLogs={liveLogs}
                    hours={hours}
                    setActiveTab={setActiveTab}
                    onRefreshStats={pollOverview}
                />
            )}
            {activeTab === "threats" && (
                <ThreatsTab
                    threatsData={threatsData}
                    threatsLoading={threatsLoading}
                    setSelectedLogId={setSelectedLogId}
                    onRefresh={pollThreats}
                />
            )}
            {activeTab === "traffic" && (
                <TrafficTab
                    trafficData={trafficData}
                    trafficLoading={trafficLoading}
                    onRefresh={pollTraffic}
                />
            )}
            {activeTab === "users" && (
                <UsersTab
                    usersData={usersData}
                    usersLoading={usersLoading}
                    onRefresh={pollUsers}
                />
            )}
            {activeTab === "apps" && (
                <AppsTab
                    appsData={appsData}
                    appsLoading={appsLoading}
                    onRefresh={pollApps}
                />
            )}
            {activeTab === "logs" && (
                <LogsTab
                    setLiveLogs={setLiveLogs}
                    actionFilter={actionFilter}
                    setActionFilter={setActionFilter}
                    severityFilter={severityFilter}
                    setSeverityFilter={setSeverityFilter}
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    debouncedSearch={debouncedSearch}
                    isLive={isLive}
                    setIsLive={setIsLive}
                    showColumnSelector={showColumnSelector}
                    setShowColumnSelector={setShowColumnSelector}
                    visibleColumns={visibleColumns}
                    setVisibleColumns={setVisibleColumns}
                    isLogsLoading={isLogsLoading}
                    activeLogs={activeLogs}
                    setSelectedLogId={setSelectedLogId}
                    limit={limit}
                    setLimit={setLimit}
                    setCurrentPage={setCurrentPage}
                    currentPage={currentPage}
                    startRecord={startRecord}
                    endRecord={endRecord}
                    totalRecords={totalRecords}
                    totalPages={totalPages}
                />
            )}

            {/* Slide-out details drawer panel */}
            {renderDetailDrawer()}
        </div>
    );
}
